import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDrag, useDrop, DropTargetMonitor } from 'react-dnd';
import { FolderInput } from './FolderInput';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/Accordion';
import { AudioDto } from '@/service/type';
import { cn } from '@/utils';
import { FolderItemType } from '@/service/type/Folder';
import FolderItem from './FolderItem';
import { useLeftPanelDrawer } from '@/state/drawer-store';

interface FolderProps {
  parentId?: number;
  folder: FolderItemType;
  moveFolder: (draggedId: number, parentFolderId?: number, nextFolderId?: number) => void;
  onAddAudio: (audio: AudioDto, dropId: number) => void;
  onCreateFolder?: (name: string, parentId?: number) => void;
  onRename: (name: string, id: number) => void;
  onDelete: (id: number) => void;
  onDownloadFolder: (name: string, id: number) => void;
  onHoveredChanged?: (isHovered: boolean) => void;
}

type DropItem = FolderItemType;

const Folder: React.FC<FolderProps> = ({
  folder,
  parentId,
  moveFolder,
  onCreateFolder,
  onAddAudio,
  onRename,
  onHoveredChanged,
  onDelete,
  onDownloadFolder,
}) => {
  const isChildren = !!parentId;
  const curRef = useRef<HTMLDivElement | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'FOLDER',
    item: { ...folder, type: 'FOLDER' },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));
  const [childrenHoveredStatus, setChildrenHoveredStatus] = useState<boolean[]>([]);

  const isChildrenHasHovered = useMemo(() => {
    return childrenHoveredStatus.some((isHovered) => isHovered);
  }, [childrenHoveredStatus]);

  useEffect(() => {
    onHoveredChanged?.(isHovered);
  }, [isHovered]);

  const getDroppedInfo = (draggedItem: FolderItemType, monitor: DropTargetMonitor) => {
    const draggedRect = monitor.getSourceClientOffset();
    const hoverRect = curRef.current?.getBoundingClientRect();
    if (draggedItem.id === folder.id) {
      return;
    }
    if (draggedItem.child_folder?.some((children) => children.id === folder.id)) {
      return;
    }
    if (draggedRect && hoverRect) {
      const isDroppedAbove = draggedRect?.y < hoverRect?.y;
      return {
        draggedItemId: draggedItem.id,
        droppedItemId: folder.id,
        isDroppedAbove: isDroppedAbove,
      };
    }
    return;
  };
  const [isDropped, setIsDropped] = useState(false);
  const [isDroppedAbove, setIsDroppedAbove] = useState<boolean | undefined>(undefined);

  const [, drop] = useDrop<DropItem>(
    () => ({
      // accept: isChildren ? ['AUDIO', 'FOLDER'] : ['FOLDER', 'AUDIO'],
      accept: isChildren ? ['AUDIO'] : ['AUDIO', 'FOLDER'],

      hover(item: any, monitor: DropTargetMonitor) {
        if (item.type === 'AUDIO') {
          if (!isChildrenHasHovered) {
            setIsHovered(true);
          } else {
            setIsHovered(false);
          }
        }
        if (item.type === 'FOLDER') {
          const { isDroppedAbove } = getDroppedInfo(item, monitor) || {};
          if (isDroppedAbove) {
            setIsDropped(true);
            setIsDroppedAbove(isDroppedAbove);
          }
        }
      },
      collect: (monitor: DropTargetMonitor) => {
        if (!monitor.isOver()) {
          setIsHovered(false);
          setIsDropped(false);
          setIsDroppedAbove(undefined);
        }
      },

      drop(item: any, monitor: DropTargetMonitor) {
        let draggedItem;

        switch (item.type) {
          case 'FOLDER': {
            const { draggedItemId, droppedItemId, isDroppedAbove } = getDroppedInfo(item, monitor) ?? {};
            if (draggedItemId !== undefined && droppedItemId !== undefined && isDroppedAbove !== undefined) {
              moveFolder(draggedItemId, parentId, droppedItemId);
            }
            break;
          }

          case 'AUDIO':
            if (!isChildrenHasHovered) {
              draggedItem = item as AudioDto;
              onAddAudio(draggedItem, folder.id);
            }
            break;
          default:
            break;
        }
      },
    }),
    [childrenHoveredStatus, onAddAudio, isChildren],
  );
  const [ifShowRenameInput, setIfShowRenameInput] = useState<boolean>(false);

  const [ifShowCreateSubfolder, setIfShowCreateSubfolder] = useState<boolean>(false);

  const { setIsDrawerShow } = useLeftPanelDrawer();

  const renderFolderItem = () => {
    return (
      <FolderItem
        onFolderItemClick={() => setIsDrawerShow(false)}
        isDragging={isDragging}
        isDropped={isDropped}
        isDroppedAbove={isDroppedAbove}
        isHovered={isHovered}
        isChildren={isChildren}
        folder={folder}
        onClickCreateSubfolder={() => {
          setIsToggleOpen(true);
          setIfShowCreateSubfolder(true);
        }}
        onClickRename={() => {
          setIfShowRenameInput(true);
        }}
        onClickDelete={() => {
          onDelete(folder.id);
        }}
        onDownloadFolder={() => {
          onDownloadFolder(folder.name, folder.id);
        }}
      />
    );
  };
  const [isToggleOpen, setIsToggleOpen] = useState(false);

  const toggleAccordion = () => {
    setIsToggleOpen(!isToggleOpen);
  };

  if (ifShowRenameInput) {
    return (
      <FolderInput
        placeholder={folder.name}
        onClose={() => {
          setIfShowRenameInput(false);
        }}
        onEnter={(val) => {
          if (val.trim()) {
            onRename?.(val, folder.id);
          }
          setIfShowRenameInput(false);
        }}
      />
    );
  }

  return (
    <div
      ref={(node) => {
        curRef.current = node;
        if (isChildren) {
          drop(node);
        } else {
          drag(drop(node));
        }
      }}
      className={cn(`relative cursor-pointer text-black  ${isDragging ? ' opacity-40 text-jen-mid-grey' : ' opacity-100'}`)}
    >
      {(isChildren || !folder?.child_folder?.length) && !ifShowCreateSubfolder ? (
        <div className="group/folder w-full  hover:bg-jen-lightest-grey-100 hover:rounded-jen-spacing-4">{renderFolderItem()}</div>
      ) : (
        <Accordion type="single" collapsible value={isToggleOpen ? 'item-1' : ''}>
          <AccordionItem value={'item-1'}>
            <AccordionTrigger
              chevIconColor="#9a9d9f"
              onClick={toggleAccordion}
              className={cn('group/folder   hover:bg-jen-lightest-grey-100 hover:rounded-jen-spacing-4')}
            >
              {renderFolderItem()}
            </AccordionTrigger>
            <AccordionContent className="ml-4">
              {ifShowCreateSubfolder && (
                <FolderInput
                  placeholder="New sub folder"
                  onClose={() => {
                    setIfShowCreateSubfolder(false);
                  }}
                  onEnter={(val) => {
                    onCreateFolder?.(val, folder.id);
                    setIfShowCreateSubfolder(false);
                  }}
                />
              )}
              {(folder.child_folder?.length ?? 0) > 0 && (
                <div>
                  {folder.child_folder?.map((child, i) => (
                    <Folder
                      onHoveredChanged={(val) => {
                        const newState = [...childrenHoveredStatus];
                        newState[i] = val;
                        setChildrenHoveredStatus(newState);
                      }}
                      onDelete={onDelete}
                      onRename={onRename}
                      onAddAudio={onAddAudio}
                      parentId={folder.id}
                      key={child.id}
                      folder={child}
                      moveFolder={moveFolder}
                      onDownloadFolder={onDownloadFolder}
                    />
                  ))}
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
    </div>
  );
};

export default Folder;
